// src/i18n.js - VERSION 5 LANGUES + NAMESPACES

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// 🇫🇷 Français
import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frDeposit from './locales/fr/deposit.json';
import frWithdrawal from './locales/fr/withdrawal.json';
import frTransfer from './locales/fr/transfer.json';
import frTranslation from './locales/fr/translation.json';

// 🇬🇧 English
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDeposit from './locales/en/deposit.json';
import enWithdrawal from './locales/en/withdrawal.json';
import enTransfer from './locales/en/transfer.json';
import enTranslation from './locales/en/translation.json';

// 🇪🇸 Español
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esDeposit from './locales/es/deposit.json';
import esWithdrawal from './locales/es/withdrawal.json';
import esTransfer from './locales/es/transfer.json';
import esTranslation from './locales/es/translation.json';

// 🇵🇹 Português
import ptCommon from './locales/pt/common.json';
import ptAuth from './locales/pt/auth.json';
import ptDeposit from './locales/pt/deposit.json';
import ptWithdrawal from './locales/pt/withdrawal.json';
import ptTransfer from './locales/pt/transfer.json';
import ptTranslation from './locales/pt/translation.json';

// 🇰🇪 Kiswahili
import swCommon from './locales/sw/common.json';
import swAuth from './locales/sw/auth.json';
import swDeposit from './locales/sw/deposit.json';
import swWithdrawal from './locales/sw/withdrawal.json';
import swTransfer from './locales/sw/transfer.json';
import swTranslation from './locales/sw/translation.json';

// ============================================
// 📦 Ressources par langue (avec namespaces)
// ============================================
const resources = {
    fr: {
        translation: frTranslation,
        common: frCommon,
        auth: frAuth,
        deposit: frDeposit,
        withdrawal: frWithdrawal,
        transfer: frTransfer,
    },
    en: {
        translation: enTranslation,
        common: enCommon,
        auth: enAuth,
        deposit: enDeposit,
        withdrawal: enWithdrawal,
        transfer: enTransfer,
    },
    es: {
        translation: esTranslation,
        common: esCommon,
        auth: esAuth,
        deposit: esDeposit,
        withdrawal: esWithdrawal,
        transfer: esTransfer,
    },
    pt: {
        translation: ptTranslation,
        common: ptCommon,
        auth: ptAuth,
        deposit: ptDeposit,
        withdrawal: ptWithdrawal,
        transfer: ptTransfer,
    },
    sw: {
        translation: swTranslation,
        common: swCommon,
        auth: swAuth,
        deposit: swDeposit,
        withdrawal: swWithdrawal,
        transfer: swTransfer,
    },
};

// ============================================
// 🌍 Langues supportées
// ============================================
export const SUPPORTED_LANGUAGES = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'sw', label: 'Kiswahili', flag: '🇰🇪' },
];

// ============================================
// 🎯 Mapping pays → langue
// ============================================
export const COUNTRY_TO_LANG = {
    BF: 'fr', CI: 'fr', SN: 'fr', ML: 'fr', NE: 'fr', TG: 'fr', BJ: 'fr', GN: 'fr',
    CM: 'fr', GA: 'fr', CG: 'fr', CD: 'fr', TD: 'fr', CF: 'fr',
    MA: 'fr', DZ: 'fr', TN: 'fr',
    FR: 'fr', BE: 'fr', CH: 'fr', LU: 'fr', MC: 'fr',
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es',
    PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', GW: 'pt', ST: 'pt',
    KE: 'sw', TZ: 'sw', UG: 'sw',
    US: 'en', GB: 'en', CA: 'en', AU: 'en', NG: 'en', GH: 'en', ZA: 'en', IN: 'en',
};

// ============================================
// 🔍 Détection de langue
// ============================================
const detectLanguage = () => {
    const saved = localStorage.getItem('app_language');
    if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
        return saved;
    }

    const browserLangs = navigator.languages || [navigator.language || 'fr'];
    for (const lang of browserLangs) {
        const code = lang.split('-')[0].toLowerCase();
        if (SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
            return code;
        }
    }

    return 'fr';
};

// ============================================
// 🚀 Initialisation i18next
// ============================================
i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: detectLanguage(),
        fallbackLng: 'en',
        supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),

        // 🆕 Namespaces disponibles
        ns: ['translation', 'common', 'auth', 'deposit', 'withdrawal', 'transfer'],
        defaultNS: 'translation',

        interpolation: {
            escapeValue: false,
        },

        saveMissing: process.env.NODE_ENV === 'development',
        missingKeyHandler: (lng, ns, key) => {
            console.warn(`🚨 Traduction manquante: [${lng}][${ns}] ${key}`);
        },

        returnNull: false,
    });

// 💾 Sauvegarde à chaque changement
i18n.on('languageChanged', (lng) => {
    localStorage.setItem('app_language', lng);
    document.documentElement.lang = lng;
});

export default i18n;